import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import StoreHeader from '@/components/store/StoreHeader'
import HeroBanner from '@/components/store/HeroBanner'
import StoreFooter from '@/components/store/StoreFooter'
import HomeQuickActions, { FEEDBACK_PATH, REQUEST_ITEM_PATH } from '@/components/store/HomeQuickActions'
import ShareExperienceForm from '@/components/store/ShareExperienceForm'
import RequestNewItemForm from '@/components/store/RequestNewItemForm'
import ClearanceDealsRail from '@/components/store/ClearanceDealsRail'
import CategoryItemsFeed from '@/components/store/CategoryItemsFeed'
import { useInventoryFavorites } from '@/hooks/useInventoryFavorites'
import { api, type InventoryItemNameRow } from '@/lib/api'

export default function HomePage() {
  const [inventoryItems, setInventoryItems] = useState<InventoryItemNameRow[]>([])
  const [inventoryLookupLoading, setInventoryLookupLoading] = useState(true)
  const [inventoryError, setInventoryError] = useState<string | null>(null)

  const { isFavorite, toggle } = useInventoryFavorites()
  const { pathname } = useLocation()
  const formView = pathname === FEEDBACK_PATH ? 'feedback' : pathname === REQUEST_ITEM_PATH ? 'request' : null

  useEffect(() => {
    let cancelled = false
    setInventoryLookupLoading(true)
    setInventoryError(null)
    api.storefront
      .getInventoryItemNames()
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          setInventoryItems(res.data.items)
        } else {
          setInventoryItems([])
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setInventoryError(e.message)
          setInventoryItems([])
        }
      })
      .finally(() => {
        if (!cancelled) setInventoryLookupLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-amber-50/50 to-orange-50/50">
      <StoreHeader inventoryItems={inventoryItems} inventoryLoading={inventoryLookupLoading} />

      <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <HeroBanner />

        <HomeQuickActions />

        <section className="mt-6 min-w-0 lg:mt-10">
          {formView ? (
            // `/feedback` and `/request-item`: the form takes the place of the item lists.
            <div>
              <Link
                to="/"
                className="mb-2 inline-flex items-center gap-0.5 text-xs font-medium text-stone-600 hover:text-primary-700 sm:text-sm"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Back to shopping
              </Link>
              <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                {/* key: a fresh form each visit (so a finished one doesn't stay on its thank-you screen). */}
                {formView === 'feedback' ? <ShareExperienceForm key={pathname} /> : <RequestNewItemForm key={pathname} />}
              </div>
            </div>
          ) : inventoryError ? (
            <p className="text-sm text-red-600">{inventoryError}</p>
          ) : (
            <>
              <ClearanceDealsRail
                items={inventoryItems}
                loading={inventoryLookupLoading}
                isFavorite={isFavorite}
                onToggleFavorite={toggle}
              />
              {!inventoryLookupLoading && (
                <CategoryItemsFeed items={inventoryItems} isFavorite={isFavorite} onToggleFavorite={toggle} />
              )}
            </>
          )}
        </section>

        <StoreFooter />
      </main>
    </div>
  )
}
